import { FallbackPriceTierOut } from '../js/store/api';
import flatMap from 'lodash/flatMap';
import { TFunction } from 'i18next';

export type LendingModel = 'OCOU' | 'SIM';
export type PriceLevel = 'MIN' | 'MEDIUM' | 'MAX';

export type PriceTier = {
    readonly lendingModel: LendingModel;
    readonly level: PriceLevel;
};

export type AvailabilityCheck = {
    readonly fallbackPriceTiers: ReadonlyArray<FallbackPriceTier>;
};

export type FallbackPriceTier = {
    readonly priceTier: PriceTier;
    readonly amount: number;
};

export const isValidLendingModel = (value: string | undefined): value is LendingModel => {
    switch (value) {
        case 'OCOU':
        case 'SIM':
            return true;
    }
    return false;
};

export const isValidPriceLevel = (value: string | undefined): value is PriceLevel => {
    switch (value) {
        case 'MIN':
        case 'MEDIUM':
        case 'MAX':
            return true;
    }
    return false;
};

export const comparePriceTier = (a: PriceTier, b: PriceTier) => {
    return a.lendingModel === b.lendingModel && a.level === b.level;
};

export const deserializePriceTier = (priceTier: string | undefined): PriceTier | undefined => {
    const [lendingModel, level] = (priceTier?.split('-') as [string | undefined, string | undefined]) ?? [];

    if (!isValidLendingModel(lendingModel) || !isValidPriceLevel(level)) {
        return undefined;
    }

    return {
        lendingModel,
        level
    };
};

export const serializePriceTier = (priceTier: PriceTier | undefined): string | undefined => {
    if (!priceTier || !isValidLendingModel(priceTier.lendingModel) || !isValidPriceLevel(priceTier.level)) {
        return undefined;
    }

    return `${priceTier.lendingModel}-${priceTier.level}`;
};

export const getPriceTierLabel = (priceTier: PriceTier | undefined, t: TFunction): string | undefined => {
    if (!priceTier || !isValidLendingModel(priceTier.lendingModel) || !isValidPriceLevel(priceTier.level)) {
        return '';
    }

    if (priceTier.lendingModel === 'OCOU') {
        return t('lendingModel.ocou');
    }

    return `${t('lendingModel.sim')} ${getPriceTierLevelLabel(priceTier, t)}`;
};

export const getPriceTierLevelLabel = (priceTier: PriceTier, t: TFunction) => {
    if (priceTier.lendingModel === 'OCOU') {
        return '';
    }

    switch (priceTier.level) {
        case 'MIN':
            return t('shop.priceTier.low');
        case 'MEDIUM':
            return t('shop.priceTier.medium');
        case 'MAX':
            return t('shop.priceTier.high');
        default:
            return undefined;
    }
};

export const mapFallbackPriceTierOutToFallbackPriceTier = (
    fallbackPriceTierOuts: ReadonlyArray<FallbackPriceTierOut> | undefined
): FallbackPriceTier[] => {
    return flatMap(fallbackPriceTierOuts ?? [], fallbackPriceOut => {
        const priceTier = deserializePriceTier(fallbackPriceOut.priceTier);
        const amount = fallbackPriceOut.amount;

        if (!priceTier || !amount) {
            return [];
        }

        return {
            priceTier,
            amount
        };
    });
};
